// @react
import React from 'react'
// @types
import type { ReactElement } from 'react'
import type {
  GetUserQuery,
  GetUserQueryVariables,
  GetAlgoliaContentQuery,
  GetAlgoliaContentQueryVariables,
} from 'lib/graphql/graphqlTypes'
// @graphql
import { useQuery } from '@apollo/client'
import { GET_ALGOLIA_CONTENT } from 'graphql/queries/algoliaContent.query'
import { GET_USER } from 'graphql/queries/user.query'
// @libraries
import { useMediaQuery } from '@material-ui/core'
// @components
import SkeletonLoader from '@bit/atd.web.loaders.skeleton'
// @module components
import DesktopTabs from './DesktopTabs'
import MobileTabs from './MobileTabs'
// @config
import { tabIndexes, tabsConfig, tabs, requests } from './benefitsConfig'

const fetchPolicy = 'network-only'

const BenefitTabs = (): ReactElement => {
  /******************************************
   * HOOKS
   ******************************************/

  const isMobile = useMediaQuery('(max-width: 580px)')

  /******************************************
   * GQL HOOKS
   ******************************************/

  const caseStudyPublications = useQuery<
    GetAlgoliaContentQuery,
    GetAlgoliaContentQueryVariables
  >(GET_ALGOLIA_CONTENT, {
    variables: {
      input: tabsConfig[tabs.PUBLICATIONS].requests.caseStudies.getInput(
        isMobile,
      ),
    },
    fetchPolicy,
  })

  const tdMagazinePublications = useQuery<
    GetAlgoliaContentQuery,
    GetAlgoliaContentQueryVariables
  >(GET_ALGOLIA_CONTENT, {
    variables: {
      input: tabsConfig[tabs.PUBLICATIONS].requests.tdMagazines.getInput(
        isMobile,
      ),
    },
    fetchPolicy,
  })

  const whitePaperPublications = useQuery<
    GetAlgoliaContentQuery,
    GetAlgoliaContentQueryVariables
  >(GET_ALGOLIA_CONTENT, {
    variables: {
      input: tabsConfig[tabs.PUBLICATIONS].requests.whitePapers.getInput(
        isMobile,
      ),
    },
    fetchPolicy,
  })

  const videos = useQuery<
    GetAlgoliaContentQuery,
    GetAlgoliaContentQueryVariables
  >(GET_ALGOLIA_CONTENT, {
    variables: {
      input: tabsConfig[tabs.VIDEOS].requests.videos.getInput(isMobile),
    },
    fetchPolicy,
  })

  const workGuidesResources = useQuery<
    GetAlgoliaContentQuery,
    GetAlgoliaContentQueryVariables
  >(GET_ALGOLIA_CONTENT, {
    variables: {
      input: tabsConfig[
        tabs.PERFORMANCE_RESOURCES
      ].requests.workGuides.getInput(isMobile),
    },
    fetchPolicy,
  })

  const templateToolsResources = useQuery<
    GetAlgoliaContentQuery,
    GetAlgoliaContentQueryVariables
  >(GET_ALGOLIA_CONTENT, {
    variables: {
      input: tabsConfig[
        tabs.PERFORMANCE_RESOURCES
      ].requests.templateTools.getInput(isMobile),
    },
    fetchPolicy,
  })

  const newsletterArticles = useQuery<
    GetAlgoliaContentQuery,
    GetAlgoliaContentQueryVariables
  >(GET_ALGOLIA_CONTENT, {
    variables: {
      input: tabsConfig[tabs.ARTICLES].requests.newsletter.getInput(isMobile),
    },
    fetchPolicy,
  })

  const tdMagazineArticles = useQuery<
    GetAlgoliaContentQuery,
    GetAlgoliaContentQueryVariables
  >(GET_ALGOLIA_CONTENT, {
    variables: {
      input: tabsConfig[tabs.ARTICLES].requests.tdMagazineArticles.getInput(
        isMobile,
      ),
    },
    fetchPolicy,
  })

  const webinars = useQuery<
    GetAlgoliaContentQuery,
    GetAlgoliaContentQueryVariables
  >(GET_ALGOLIA_CONTENT, {
    variables: {
      input: tabsConfig[tabs.WEBINARS].requests.webinars.getInput(isMobile),
    },
    fetchPolicy,
  })

  const userData = useQuery<GetUserQuery, GetUserQueryVariables>(GET_USER, {
    fetchPolicy: 'cache-first',
  })

  const allData = [
    userData,
    caseStudyPublications,
    tdMagazinePublications,
    whitePaperPublications,
    videos,
    workGuidesResources,
    templateToolsResources,
    newsletterArticles,
    tdMagazineArticles,
    webinars,
  ]

  /******************************************
   * RENDERS
   ******************************************/

  if (!allData.every((item) => !item.loading)) {
    return <SkeletonLoader />
  }

  const user = userData?.data?.user || undefined

  const composedData = [
    {
      tabIndex: tabIndexes.PUBLICATIONS,
      tab: tabs.PUBLICATIONS,
      index: 'publications',
      data: {
        [requests.CASE_STUDIES]: caseStudyPublications,
        [requests.TD_MAGAZINES]: tdMagazinePublications,
        [requests.WHITE_PAPERS]: whitePaperPublications,
      },
    },
    {
      tabIndex: tabIndexes.VIDEOS,
      tab: tabs.VIDEOS,
      index: 'videos',
      data: { [requests.VIDEOS]: videos },
    },
    {
      tabIndex: tabIndexes.PERFORMANCE_RESOURCES,
      tab: tabs.PERFORMANCE_RESOURCES,
      index: 'performance_resources',
      data: {
        [requests.WORK_GUIDES]: workGuidesResources,
        [requests.TEMPLATE_TOOLS]: templateToolsResources,
      },
    },
    {
      tabIndex: tabIndexes.ARTICLES,
      tab: tabs.ARTICLES,
      index: 'articles',
      data: {
        [requests.NEWS_LETTER]: newsletterArticles,
        [requests.TD_MAGAZINE_ARTICLES]: tdMagazineArticles,
      },
    },
    {
      tabIndex: tabIndexes.WEBINARS,
      index: 'webinars',
      tab: tabs.WEBINARS,
      data: { [requests.WEBINARS]: webinars },
    },
  ]

  const tabsItems = composedData.map(({ data, ...rest }) => {
    //@ts-ignore
    return {
      data: Object.keys(data).reduce(
        (items, key) => ({ ...items, [key]: data[key]?.data?.algoliaContent }),
        {},
      ),
      ...rest,
    }
  })

  const productCode = user?.membership?.currentMembership?.memberType || ''

  return isMobile ? (
    <MobileTabs tabsItems={tabsItems} productCode={productCode} />
  ) : (
    <DesktopTabs tabsItems={tabsItems} productCode={productCode} />
  )
}

export default BenefitTabs
